import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import Layout, { PageHeader, Surface, StatusPill } from "@/components/Layout";
import { KybDocumentGallery } from "@/components/KybDocumentGallery";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiErrors";

export default function KybQueue() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/kyb/queue");
      const list = r.data.items || [];
      setItems(list);
      setSelected((prev) => {
        if (!list.length) return null;
        if (prev) {
          const still = list.find((m) => m.id === prev.id);
          if (still) return still;
        }
        return list[0];
      });
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not load KYB queue"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const review = async (decision) => {
    if (!selected) return;
    if (decision === "rejected" && !reason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    try {
      await api.post(`/admin/kyb/merchant/${selected.id}/review`, {
        decision,
        reason: reason.trim() || null,
      });
      toast.success(decision === "approved" ? "Merchant approved" : "Merchant rejected");
      setReason("");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Review failed"));
    }
  };

  const docCount = (selected?.documents || []).length;
  const withFiles = (selected?.documents || []).filter((d) => d.file_url).length;

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="kyb-queue-page">
        <PageHeader
          title="KYB Queue"
          subtitle={
            loading
              ? "Loading…"
              : `${items.length} merchant${items.length === 1 ? "" : "s"} awaiting review.`
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Surface className="lg:col-span-1 overflow-hidden">
            <ul className="divide-y divide-zinc-100 max-h-[70vh] overflow-y-auto">
              {items.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(m)}
                    data-testid={`kyb-row-${m.id}`}
                    className={`w-full text-left p-4 transition-colors duration-150 ${
                      selected?.id === m.id ? "bg-zinc-100" : "hover:bg-zinc-50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium truncate">{m.company_name}</div>
                      <StatusPill value={m.kyb_status} />
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5 truncate">{m.email}</div>
                    <div className="text-[10px] text-zinc-400 mt-1">
                      {(m.documents || []).length} document(s)
                    </div>
                  </button>
                </li>
              ))}
              {!loading && items.length === 0 && (
                <li className="p-6 text-sm text-zinc-500 text-center">Queue is empty.</li>
              )}
            </ul>
          </Surface>

          {selected && (
            <Surface className="lg:col-span-2 p-6">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Merchant</div>
                  <div className="font-display text-2xl tracking-tight font-medium">{selected.company_name}</div>
                  <div className="text-sm text-zinc-500 mt-0.5">{selected.email}</div>
                </div>
                <div className="flex gap-2">
                  <StatusPill value={selected.status} />
                  <StatusPill value={selected.kyb_status} />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-6">
                <KV k="Country" v={selected.country || "—"} />
                <KV k="Contact" v={selected.contact_name || "—"} />
                <KV k="Risk score" v={String(selected.risk_score ?? 50)} />
                <KV k="Source" v={selected.signup_source || "—"} />
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">
                      Submitted documents
                    </div>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {docCount === 0
                        ? "No uploads yet"
                        : `${withFiles} of ${docCount} with preview`}
                    </p>
                  </div>
                </div>
                <KybDocumentGallery
                  documents={selected.documents}
                  emptyMessage="No documents uploaded yet."
                />
              </div>

              <div className="border-t border-zinc-100 pt-5">
                <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">
                  Review note (required for rejection)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="kyb-review-reason"
                  className="w-full p-3 text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none"
                  rows={3}
                  placeholder="Optional for approval. Required when rejecting."
                />
                <div className="flex justify-end gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => review("rejected")}
                    data-testid="kyb-reject-btn"
                    className="h-9 px-4 text-sm border border-zinc-200 rounded-sm hover:bg-red-50 hover:text-red-700 hover:border-red-200 transition-colors duration-150"
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    onClick={() => review("approved")}
                    data-testid="kyb-approve-btn"
                    className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-sm hover:bg-zinc-800 transition-colors duration-150"
                  >
                    Approve & activate
                  </button>
                </div>
              </div>
            </Surface>
          )}
        </div>
      </div>
    </Layout>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">{k}</div>
      <div className="mt-1 text-sm">{v}</div>
    </div>
  );
}
